import { RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { HistoryInfoType } from '@/types/types_history';
import { useHistoryPanel } from './useHistoryPanel';
import { getStepIndex } from './util_history';

interface HistoryPanelProps {
  show: boolean;
  onClose: () => void;
  history: HistoryInfoType | undefined;
  /** The `{collection}history` collection holding this document's archived steps. */
  historyCollectionName: string;
}

/**
 * Slide-over listing the document's edit history: a chronological audit log,
 * newest first. Mirrors the portal's HistoryPanel.
 *
 * The live document only keeps the last PRESERVE_HISTORY_STEPS steps inline;
 * "Show more" walks back through the archive collection written by the
 * historized-document Cloud Function (see useHistoryPanel).
 *
 * Steps loaded from the archive are held for one document, so callers must give
 * this a `key` of the document id — otherwise navigating between two records
 * without unmounting would show the first record's older steps under the
 * second.
 */
export function HistoryPanel({ show, onClose, history, historyCollectionName }: HistoryPanelProps) {
  const { historySteps, nextStepToLoad, historyLoading, loadMoreHistory } = useHistoryPanel(
    historyCollectionName,
    history?.steps,
  );

  const steps = [...historySteps.entries()]
    .map(([key, step]) => ({ num: getStepIndex(key), step }))
    .sort((a, b) => b.num - a.num);

  return (
    <div
      className={cn(
        'tw:fixed tw:top-0 tw:right-0 tw:z-50 tw:flex tw:h-screen tw:w-full tw:flex-col tw:bg-white tw:sm:w-[24em]',
        'tw:border-l tw:border-ed-umber/20 tw:transition-transform tw:duration-300 tw:ease-in-out',
        show ? 'tw:translate-x-0 tw:shadow-[-2px_0_16px_rgba(0,0,0,0.15)]' : 'tw:translate-x-full',
      )}
      aria-hidden={!show}
    >
      <div className='tw:flex tw:items-center tw:justify-between tw:border-b tw:border-ed-umber/10 tw:px-4 tw:py-3'>
        <h2 className='tw:font-serif tw:text-lg tw:text-ed-black'>Edit history</h2>
        <button
          type='button'
          onClick={onClose}
          title='Close'
          className='tw:rounded tw:p-1 tw:text-ed-stone tw:transition-colors tw:hover:bg-ed-umber/10 tw:hover:text-ed-black'
        >
          <X className='tw:size-4' />
        </button>
      </div>

      <div className='tw:flex-1 tw:overflow-y-auto'>
        {steps.length === 0 ? (
          <p className='tw:px-4 tw:py-10 tw:text-center tw:text-sm tw:text-ed-stone'>No changes recorded yet.</p>
        ) : (
          <ul className='tw:divide-y tw:divide-ed-umber/10'>
            {steps.map(({ num, step }) => {
              const isCurrent = num === history?.currentStep;
              return (
                <li
                  key={step.stepId}
                  className={cn(
                    'tw:flex tw:items-baseline tw:gap-3 tw:px-4 tw:py-2.5 tw:text-sm',
                    isCurrent && 'tw:bg-ed-gold/10',
                  )}
                >
                  <span className='tw:w-6 tw:shrink-0 tw:text-right tw:text-xs tw:text-ed-stone'>{num}</span>
                  <div className='tw:min-w-0 tw:flex-1'>
                    <p className='tw:flex tw:items-center tw:gap-1.5 tw:text-ed-black'>
                      {step.isUndoStep && <RotateCcw className='tw:size-3 tw:shrink-0 tw:text-ed-stone' />}
                      {step.stepAction}
                    </p>
                    <p className='tw:truncate tw:text-xs tw:text-ed-stone' title={step.userModified?.email}>
                      {/* Older steps predate `userModified`, hence the fallback. */}
                      {step.userModified?.name ?? 'Unknown user'} · {new Date(step.dateModified).toLocaleString()}
                      {isCurrent && <span className='tw:ml-2 tw:text-ed-gold'>current</span>}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {steps.length > 0 && (
          <div className='tw:px-4 tw:py-5 tw:text-center'>
            {nextStepToLoad ? (
              <Button type='button' variant='outline' size='sm' loading={historyLoading} onClick={() => void loadMoreHistory()}>
                Show more
              </Button>
            ) : (
              <span className='tw:text-xs tw:text-ed-stone'>All history loaded</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
